import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, Loader2, Plus, RefreshCw, Search, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { formatCurrency } from '@/lib/format'
import { payrollPaymentService } from '@/services/payrollPaymentService'
import { getSalaryById } from '@/services/payrollService'
import { PaySlipModal } from '@/modules/hr/components/PaySlipModal'
import type {
  PayrollPaymentListItem,
  PayrollPaymentSummary,
  PayrollPaymentSummaryEmployee,
  SalaryById,
} from '@/api/types/payroll.types'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function toInputMonthYear(ym?: string): string {
  if (!ym || ym.length < 5) return getDefaultMonthYear()
  const parts = ym.split('-').filter(Boolean)
  if (parts.length < 2) return getDefaultMonthYear()
  if (parts[0].length === 4) return `${parts[0]}-${parts[1].padStart(2, '0')}`
  return `${parts[1]}-${parts[0].padStart(2, '0')}`
}

function formatMonthYearLabel(ym?: string, locale?: string): string {
  if (!ym || ym.length < 5) return '—'
  const parts = ym.split('-').filter(Boolean)
  if (parts.length < 2) return ym
  let monthNum: number
  let yearStr: string
  if (parts[0].length === 4) {
    yearStr = parts[0]
    monthNum = parseInt(parts[1], 10)
  } else {
    monthNum = parseInt(parts[0], 10)
    yearStr = parts[1]
  }
  if (monthNum >= 1 && monthNum <= 12 && yearStr) {
    const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
    return new Date(Number(yearStr), monthNum - 1, 1).toLocaleDateString(loc, {
      month: 'long',
      year: 'numeric',
    })
  }
  return ym
}

function formatPayDay(dateString?: string, locale?: string): { day: string; rest: string } {
  if (!dateString) return { day: '—', rest: '' }
  try {
    const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
    const d = new Date(dateString)
    if (Number.isNaN(d.getTime())) return { day: '—', rest: '' }
    return {
      day: d.toLocaleDateString(loc, { day: '2-digit' }),
      rest: d.toLocaleDateString(loc, { month: 'short', year: 'numeric' }).toUpperCase(),
    }
  } catch {
    return { day: String(dateString).slice(0, 10), rest: '' }
  }
}

function money(value?: number): string {
  return formatCurrency(Number(value ?? 0))
}

function employeeLabel(row: PayrollPaymentSummaryEmployee, isAr: boolean, fallback: string): string {
  return (isAr ? row.employeeNameAr || row.employeeName : row.employeeName || row.employeeNameAr) || fallback
}

function employeeCode(row: PayrollPaymentSummaryEmployee): string {
  return row.empCode || (row.empId ? `EMP-${row.empId}` : '—')
}

function hasTaxAmount(value?: number): boolean {
  return Number(value ?? 0) !== 0
}

function toSalaryByIdFromSummary(
  row: PayrollPaymentSummaryEmployee,
  monthYear?: string,
): SalaryById {
  return {
    id: row.salaryId,
    empId: row.empId,
    empCode: row.empCode,
    employeeName: row.employeeName,
    salaryMonthYear: monthYear,
    basicSalary: row.basicSalary,
    housingAllowance: row.housingAllowance,
    transpotationAllowance: row.transportationAllowance,
    mealAllowance: row.mealAllowance,
    monthlyBonus: row.bonus,
    otAmount: row.overtime,
    totalDeduction: row.deductions,
    totalSalary: row.netPay,
    gosiDeduction: row.gosiDeduction,
    loanDeduction: row.loanDeduction,
    absentDeduction: row.absentDeduction,
    lateDeduction: row.lateDeduction,
    earlyOutDeduction: row.earlyOutDeduction,
    otherDeductions: row.otherDeductions,
    taxDeduction: row.taxDeduction,
    familyTaxMonthly: row.familyTaxMonthly,
    otherTax: row.otherTax,
    payDays: 30,
  }
}

interface PayrollPaymentDashboardProps {
  initialPaymentId?: number
  showAddButton?: boolean
  showBackButton?: boolean
}

export const PayrollPaymentDashboard: React.FC<PayrollPaymentDashboardProps> = ({
  initialPaymentId,
  showAddButton = false,
  showBackButton = false,
}) => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language
  const isAr = locale.toLowerCase().startsWith('ar')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = Number(user?.selectedBranch?.branchId ?? 0)

  const [monthYear, setMonthYear] = useState(getDefaultMonthYear())
  const [payments, setPayments] = useState<PayrollPaymentListItem[]>([])
  const [selectedPaymentId, setSelectedPaymentId] = useState<number>(0)
  const [summary, setSummary] = useState<PayrollPaymentSummary | null>(null)
  const [loadingPayments, setLoadingPayments] = useState(false)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [posting, setPosting] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<'employees' | 'taxes'>('employees')
  const [monthReady, setMonthReady] = useState(!initialPaymentId)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailData, setDetailData] = useState<SalaryById | null>(null)

  useEffect(() => {
    if (!branchId || !initialPaymentId) {
      setMonthReady(true)
      return
    }
    let cancelled = false
    payrollPaymentService
      .getById(initialPaymentId, branchId)
      .then((master) => {
        if (cancelled) return
        const ym = toInputMonthYear((master as any)?.payrollMonthYear)
        setMonthYear(ym)
        setSelectedPaymentId(initialPaymentId)
        setMonthReady(true)
      })
      .catch(() => {
        if (!cancelled) setMonthReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [branchId, initialPaymentId])

  useEffect(() => {
    if (!branchId || !monthReady || !monthYear) return
    let cancelled = false
    setLoadingPayments(true)
    payrollPaymentService
      .getByMonthYear(monthYear, branchId)
      .then((list) => {
        if (cancelled) return
        setPayments(list)
        setSelectedPaymentId((prev) => {
          if (prev && list.some((p) => Number(p.id) === prev)) return prev
          if (initialPaymentId && list.some((p) => Number(p.id) === initialPaymentId)) return initialPaymentId
          if (list.length === 1) return Number(list[0].id ?? 0)
          if (list.length > 1) return Number(list[0].id ?? 0)
          return 0
        })
      })
      .catch((err: any) => {
        if (cancelled) return
        setPayments([])
        setSelectedPaymentId(0)
        const msg = String(err?.response?.data?.message || err?.message || '').toLowerCase()
        if (msg.includes('not found') || msg.includes('no payroll')) return
        toast.error(err?.response?.data?.message || err?.message || t('payrollPaymentPage.fetchFailed'))
      })
      .finally(() => {
        if (!cancelled) setLoadingPayments(false)
      })
    return () => {
      cancelled = true
    }
  }, [branchId, monthYear, monthReady, initialPaymentId, t])

  useEffect(() => {
    if (!branchId || !selectedPaymentId) {
      setSummary(null)
      return
    }
    let cancelled = false
    setLoadingSummary(true)
    payrollPaymentService
      .getSummary(selectedPaymentId, branchId)
      .then((data) => {
        if (!cancelled) setSummary(data)
      })
      .catch((err: any) => {
        if (cancelled) return
        setSummary(null)
        toast.error(err?.response?.data?.message || err?.message || t('payrollPaymentPage.loadFailed'))
      })
      .finally(() => {
        if (!cancelled) setLoadingSummary(false)
      })
    return () => {
      cancelled = true
    }
  }, [branchId, selectedPaymentId, t])

  const selectedPayment = useMemo(
    () => payments.find((p) => Number(p.id) === selectedPaymentId),
    [payments, selectedPaymentId],
  )

  const isPosted = Boolean(summary?.isPosted ?? selectedPayment?.isPosted)
  const totals = summary?.totals
  const payDay = formatPayDay(summary?.paymentDate ?? selectedPayment?.paymentDate, locale)
  const hasTaxes = Boolean(
    hasTaxAmount(totals?.taxes) ||
      (summary?.employees ?? []).some(
        (row) =>
          hasTaxAmount(row.taxes) ||
          hasTaxAmount(row.taxDeduction) ||
          hasTaxAmount(row.familyTaxMonthly) ||
          hasTaxAmount(row.otherTax),
      ),
  )
  const hasRemaining = Boolean(
    hasTaxAmount(totals?.remainingAmount) ||
      (summary?.employees ?? []).some((row) => hasTaxAmount(row.remainingAmount)),
  )

  const filteredEmployees = useMemo(() => {
    const rows = summary?.employees ?? []
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((row) => {
      const name = employeeLabel(row, isAr, '').toLowerCase()
      return name.includes(q) || String(row.empId).includes(q)
    })
  }, [summary?.employees, search, isAr])

  const openEmployeeDetail = async (row: PayrollPaymentSummaryEmployee) => {
    const fallback = toSalaryByIdFromSummary(row, summary?.payrollMonthYear || monthYear)
    setDetailOpen(true)
    setDetailLoading(true)
    setDetailData(fallback)
    try {
      const salaryId = Number(row.salaryId ?? 0)
      if (salaryId > 0) {
        const detail = await getSalaryById(salaryId)
        setDetailData({
          ...fallback,
          ...detail,
          employeeName: detail.employeeName || fallback.employeeName,
          empCode: detail.empCode || fallback.empCode,
        })
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('payrollPaymentPage.employeeDetailFailed'))
    } finally {
      setDetailLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedPaymentId || !branchId) return
    if (isPosted) {
      toast.error(t('payrollPaymentPage.postedCannotDelete'))
      return
    }
    const ok = await showDeleteConfirm(undefined, t('payrollPaymentPage.deleteConfirm'))
    if (!ok) return
    try {
      await payrollPaymentService.delete(selectedPaymentId, branchId)
      await showSuccess(t('payrollPaymentPage.deletedTitle'), t('payrollPaymentPage.deletedMessage'), 2000)
      const remaining = payments.filter((p) => Number(p.id) !== selectedPaymentId)
      setPayments(remaining)
      setSelectedPaymentId(Number(remaining[0]?.id ?? 0))
      if (remaining.length === 0) setSummary(null)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('payrollPaymentPage.deleteFailed'))
    }
  }

  const handlePost = async () => {
    if (!selectedPaymentId || !branchId || posting || isPosted) return
    setPosting(true)
    try {
      await payrollPaymentService.postData(selectedPaymentId, branchId)
      await showSuccess(t('payrollPaymentPage.postedTitle'), t('payrollPaymentPage.postedMessage'), 2000)
      setPayments((prev) =>
        prev.map((p) => (Number(p.id) === selectedPaymentId ? { ...p, isPosted: true } : p)),
      )
      setSummary((prev) => (prev ? { ...prev, isPosted: true } : prev))
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Post failed')
    } finally {
      setPosting(false)
    }
  }

  const handleRefresh = async () => {
    if (!selectedPaymentId || !branchId || refreshing || isPosted) return
    setRefreshing(true)
    try {
      await payrollPaymentService.refresh(selectedPaymentId, branchId)
      const [list, data] = await Promise.all([
        payrollPaymentService.getByMonthYear(monthYear, branchId),
        payrollPaymentService.getSummary(selectedPaymentId, branchId),
      ])
      setPayments(list)
      setSummary(data)
      await showSuccess(t('payrollPaymentPage.refreshedTitle'), t('payrollPaymentPage.refreshedMessage'), 2000)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || t('payrollPaymentPage.refreshFailed'))
    } finally {
      setRefreshing(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('payrollPaymentPage.pageTitle')} description={t('payrollPaymentPage.summaryDescription')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('payrollPaymentPage.selectBranchList')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('payrollPaymentPage.pageTitle')} description={t('payrollPaymentPage.summaryDescription')}>
        <div className="flex flex-wrap items-center gap-2">
          {showBackButton && (
            <Button variant="outline" onClick={() => navigate('/payroll/payroll-payment')}>
              {t('payrollPaymentPage.back')}
            </Button>
          )}
          {showAddButton && !loadingPayments && payments.length === 0 && (
            <Button onClick={() => navigate('/payroll/payroll-payment/new')}>
              <Plus className="w-4 h-4 mr-2" />
              {t('payrollPaymentPage.addButton')}
            </Button>
          )}
          {selectedPaymentId > 0 && (
            <>
              {!isPosted && (
                <Button onClick={() => void handleRefresh()} disabled={refreshing || posting}>
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {t('payrollPaymentPage.refreshPayment')}
                </Button>
              )}
              <Button onClick={() => void handlePost()} disabled={isPosted || posting || refreshing}>
                {isPosted ? (
                  t('payrollPaymentPage.statusPaid')
                ) : posting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('payrollPaymentPage.columnPost')
                )}
              </Button>
              {!isPosted && (
                <Button variant="outline" onClick={() => void handleDelete()} disabled={refreshing || posting}>
                  <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                  {t('payrollPaymentPage.deleteTitle')}
                </Button>
              )}
            </>
          )}
        </div>
      </PageHeader>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-2 min-w-[180px]">
          <Label>{t('payrollPaymentPage.labelMonthYear')}</Label>
          <Input
            type="month"
            value={monthYear}
            onChange={(e) => {
              setMonthYear(e.target.value)
              setSelectedPaymentId(0)
              setSummary(null)
              setSearch('')
              setTab('employees')
            }}
            disabled={Boolean(initialPaymentId)}
          />
        </div>
        {payments.length > 1 && (
          <div className="space-y-2 min-w-[240px]">
            <Label>{t('payrollPaymentPage.labelPayment')}</Label>
            <Select
              value={selectedPaymentId ? String(selectedPaymentId) : ''}
              onValueChange={(value) => {
                setSelectedPaymentId(Number(value))
                setSearch('')
                setTab('employees')
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('payrollPaymentPage.selectPayment')} />
              </SelectTrigger>
              <SelectContent>
                {payments.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {(p.paymentNo || `#${p.id}`) + (p.isPosted ? ` — ${t('payrollPaymentPage.statusPaid')}` : '')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {(summary?.paymentNo || selectedPayment?.paymentNo) && (
          <div
            className={`pb-2 text-sm ${
              isPosted ? 'text-green-600 dark:text-green-500' : 'text-sky-500 dark:text-sky-400'
            }`}
          >
            {isPosted ? t('payrollPaymentPage.statusPaid') : t('payrollPaymentPage.draft')}
          </div>
        )}
      </div>

      {loadingPayments || loadingSummary ? (
        <Card>
          <CardContent className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : !selectedPaymentId || !summary ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {payments.length === 0 ? t('payrollPaymentPage.emptyMonth') : t('payrollPaymentPage.selectPayment')}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="rounded-xl border bg-card overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 divide-y md:divide-y-0 md:divide-x">
              <div className="p-5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('payrollPaymentPage.period')}
                </p>
                <p className="mt-3 text-lg font-semibold">
                  {formatMonthYearLabel(summary.payrollMonthYear || monthYear, locale)}
                </p>
                {(summary.paymentNo || selectedPayment?.paymentNo) && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {summary.paymentNo || selectedPayment?.paymentNo}
                  </p>
                )}
              </div>

              <div className="p-5 bg-muted/40">
                <div>
                  <p className="text-2xl font-semibold tracking-tight">{money(totals?.payrollCost)}</p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">
                    {t('payrollPaymentPage.payrollCost')}
                  </p>
                </div>
                <div className="mt-4">
                  <p className="text-2xl font-semibold tracking-tight">{money(totals?.netPay)}</p>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-1">
                    {t('payrollPaymentPage.employeesNetPay')}
                  </p>
                </div>
              </div>

              <div className="p-5 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('payrollPaymentPage.payDay')}
                </p>
                <p className="mt-2 text-4xl font-semibold leading-none">{payDay.day}</p>
                <p className="mt-2 text-sm text-muted-foreground">{payDay.rest}</p>
                <p className="mt-3 text-sm font-medium">
                  {t('payrollPaymentPage.employeesCount', { count: summary.employeeCount })}
                </p>
              </div>

              <div className="p-5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                  {hasTaxes
                    ? t('payrollPaymentPage.taxesAndDeductions')
                    : t('payrollPaymentPage.preTaxDeductions')}
                </p>
                <div className="space-y-2 text-sm">
                  {hasTaxes && (
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{t('payrollPaymentPage.taxes')}</span>
                      <span className="font-medium">{money(totals?.taxes)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">{t('payrollPaymentPage.preTaxDeductions')}</span>
                    <span className="font-medium">{money(totals?.deductions)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">{t('payrollPaymentPage.amountPaidThisPayment')}</span>
                    <span className="font-medium">{money(totals?.amountPaid ?? summary.amount)}</span>
                  </div>
                  {hasRemaining && (
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{t('payrollPaymentPage.remainingThisPayment')}</span>
                      <span className="font-medium">{money(totals?.remainingAmount)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="border-b">
            <div className="flex gap-6">
              <button
                type="button"
                className={`pb-3 text-sm font-medium ${
                  tab === 'employees'
                    ? 'border-b-2 border-primary text-foreground'
                    : 'text-muted-foreground'
                }`}
                onClick={() => setTab('employees')}
              >
                {t('payrollPaymentPage.tabEmployeeSummary')}
              </button>
              <button
                type="button"
                className={`pb-3 text-sm font-medium ${
                  tab === 'taxes'
                    ? 'border-b-2 border-primary text-foreground'
                    : 'text-muted-foreground'
                }`}
                onClick={() => setTab('taxes')}
              >
                {hasTaxes
                  ? t('payrollPaymentPage.tabTaxesDeductions')
                  : t('payrollPaymentPage.preTaxDeductions')}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium">{t('payrollPaymentPage.allEmployees')}</p>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder={t('payrollPaymentPage.searchEmployee')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="rounded-md border overflow-x-auto">
            {tab === 'employees' ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('payrollPaymentPage.colEmpCode')}</TableHead>
                    <TableHead>{t('payrollPaymentPage.colEmployeeName')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colGrossPay')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colAllowances')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colDeductions')}</TableHead>
                    {hasTaxes && (
                      <TableHead className="text-right">{t('payrollPaymentPage.colTaxes')}</TableHead>
                    )}
                    <TableHead className="text-right">{t('payrollPaymentPage.colBonusOt')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colNetPay')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colAmountPaid')}</TableHead>
                    {hasRemaining && (
                      <TableHead className="text-right">{t('payrollPaymentPage.colRemainingAmount')}</TableHead>
                    )}
                    <TableHead className="text-center">{t('payrollPaymentPage.columnStatus')}</TableHead>
                    <TableHead className="text-center">{t('payrollPaymentPage.columnAction')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={10 + (hasTaxes ? 1 : 0) + (hasRemaining ? 1 : 0)}
                        className="text-center text-muted-foreground py-8"
                      >
                        {t('payrollPaymentPage.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEmployees.map((row) => (
                      <TableRow key={row.lineId ?? `${row.empId}-${row.amountPaid}`}>
                        <TableCell className="font-medium">{employeeCode(row)}</TableCell>
                        <TableCell className="font-medium">
                          {employeeLabel(row, isAr, t('payrollPaymentPage.employeeFallback', { id: row.empId }))}
                        </TableCell>
                        <TableCell className="text-right">{money(row.grossPay)}</TableCell>
                        <TableCell className="text-right">{money(row.allowances)}</TableCell>
                        <TableCell className="text-right">{money(row.deductions)}</TableCell>
                        {hasTaxes && (
                          <TableCell className="text-right">{money(row.taxes)}</TableCell>
                        )}
                        <TableCell className="text-right">{money(row.bonus + row.overtime)}</TableCell>
                        <TableCell className="text-right">{money(row.netPay)}</TableCell>
                        <TableCell className="text-right">{money(row.amountPaid)}</TableCell>
                        {hasRemaining && (
                          <TableCell className="text-right">
                            {hasTaxAmount(row.remainingAmount) ? money(row.remainingAmount) : ''}
                          </TableCell>
                        )}
                        <TableCell
                          className={`text-center text-sm ${
                            !isPosted
                              ? 'text-sky-500 dark:text-sky-400'
                              : hasTaxAmount(row.remainingAmount)
                                ? 'text-amber-600 dark:text-amber-500'
                                : 'text-green-600 dark:text-green-500'
                          }`}
                        >
                          {!isPosted
                            ? t('payrollPaymentPage.draft')
                            : hasTaxAmount(row.remainingAmount)
                              ? t('salaryDetail.statusPartiallyPaid')
                              : t('payrollPaymentPage.statusPaid')}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                            onClick={() => void openEmployeeDetail(row)}
                            disabled={detailLoading}
                            title={t('payrollPaymentPage.viewEmployeeDetails')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('payrollPaymentPage.colEmpCode')}</TableHead>
                    <TableHead>{t('payrollPaymentPage.colEmployeeName')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colGosi')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colLoan')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colAbsent')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colLate')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colEarlyOut')}</TableHead>
                    <TableHead className="text-right">{t('payrollPaymentPage.colOtherDeductions')}</TableHead>
                    {hasTaxes && (
                      <TableHead className="text-right">{t('payrollPaymentPage.colTaxes')}</TableHead>
                    )}
                    <TableHead className="text-right">{t('payrollPaymentPage.colDeductions')}</TableHead>
                    <TableHead className="text-center">{t('payrollPaymentPage.columnAction')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={hasTaxes ? 11 : 10} className="text-center text-muted-foreground py-8">
                        {t('payrollPaymentPage.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEmployees.map((row) => (
                      <TableRow key={`tax-${row.lineId ?? row.empId}`}>
                        <TableCell className="font-medium">{employeeCode(row)}</TableCell>
                        <TableCell className="font-medium">
                          {employeeLabel(row, isAr, t('payrollPaymentPage.employeeFallback', { id: row.empId }))}
                        </TableCell>
                        <TableCell className="text-right">{money(row.gosiDeduction)}</TableCell>
                        <TableCell className="text-right">{money(row.loanDeduction)}</TableCell>
                        <TableCell className="text-right">{money(row.absentDeduction)}</TableCell>
                        <TableCell className="text-right">{money(row.lateDeduction)}</TableCell>
                        <TableCell className="text-right">{money(row.earlyOutDeduction)}</TableCell>
                        <TableCell className="text-right">{money(row.otherDeductions)}</TableCell>
                        {hasTaxes && (
                          <TableCell className="text-right">{money(row.taxes)}</TableCell>
                        )}
                        <TableCell className="text-right">{money(row.deductions)}</TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                            onClick={() => void openEmployeeDetail(row)}
                            disabled={detailLoading}
                            title={t('payrollPaymentPage.viewEmployeeDetails')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}

      <PaySlipModal
        open={detailOpen}
        onOpenChange={setDetailOpen}
        data={detailData}
        loading={detailLoading}
      />
    </div>
  )
}
